"use client"

import type React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card } from "../ui/card-animated"

import type { FormData } from "./types"
import { TextInput } from "../ui/text-input-animated"
import { ToggleSwitch } from "../ui/toggle-switch-animated"

interface HealthQuestionnaireSectionProps {
  formData: FormData
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  showHealthWarning: boolean
  conditionalTextareaClass: string
}

const slideUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

export const HealthQuestionnaireSection: React.FC<HealthQuestionnaireSectionProps> = ({
  formData,
  handleChange,
  showHealthWarning,
  conditionalTextareaClass,
}) => {
  return (
    <Card title="Cuestionario de Salud" delay={2}>
      <div className="space-y-4">
        <motion.div variants={slideUp} className="flex items-center justify-between">
          <label className="text-gray-700 font-medium pr-3 text-sm">
            ¿Tiene o ha tenido alguna enfermedad cardíaca, respiratoria o condición médica diagnosticada que limite o
            contraindique la práctica de ejercicio físico?
          </label>
          <ToggleSwitch name="cardiac_condition" checked={formData.cardiac_condition} onChange={handleChange} />
        </motion.div>

        <motion.div variants={slideUp} className="flex items-center justify-between">
          <label className="text-gray-700 font-medium pr-3 text-sm">
            ¿Existe alguna condición física o recomendación médica actual que le impida realizar ejercicio sin
            supervisión especializada (por ejemplo, hipertensión, diabetes no controlada, problemas articulares graves,
            etc.)?
          </label>
          <ToggleSwitch name="physical_limitation" checked={formData.physical_limitation} onChange={handleChange} />
        </motion.div>

        <motion.div variants={slideUp} className="flex items-center justify-between">
          <label className="text-gray-700 font-medium pr-3 text-sm">
            ¿Se encuentra actualmente embarazada o en un embarazo de riesgo que contraindique la actividad física?
          </label>
          <ToggleSwitch name="pregnancy_risk" checked={formData.pregnancy_risk} onChange={handleChange} />
        </motion.div>

        <motion.div variants={slideUp} className="flex items-center justify-between">
          <label className="text-gray-700 font-medium text-sm">
            ¿Ha sufrido lesiones musculares, articulares o óseas graves en el pasado que puedan afectar su capacidad
            para entrenar con normalidad?
          </label>
          <ToggleSwitch name="serious_injuries" checked={formData.serious_injuries} onChange={handleChange} />
        </motion.div>

        <AnimatePresence>
          {showHealthWarning && (
            <motion.div
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -5 }}
              transition={{ duration: 0.3 }}
              className="p-3 bg-gray-50 border-l-2 border-gray-400 rounded-r-md"
            >
              <p className="text-gray-700 font-medium text-sm">
                Si presentas alguno de estos problemas a la hora de realizar ejercicio físico, por favor solicita una
                evaluación con un profesional de centro.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showHealthWarning && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TextInput
                label="Descripción (opcional)"
                name="medical_problem_description"
                value={formData.medical_problem_description}
                onChange={handleChange}
                placeholder="Si lo desea, puede proporcionar más detalles sobre su condición médica"
                isTextarea
                customClassName={conditionalTextareaClass}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Card>
  )
}
